import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ORDER_URL,
  END_POINT_PRODUCT,
  END_POINT_USERS_URL,
} from "../../common/apiUrls";
import api from "../../utils/axiosUtils";

// Async thunks
export const fetchOrderById = createAsyncThunk(
  "orderChecker/fetchOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ORDER_URL}?orderId=${orderId}`);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchRecentOrders = createAsyncThunk(
  "orderChecker/fetchRecentOrders",
  async ({ limit = 12, page = 1 }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${ORDER_URL}/checker?limit=${limit}&page=${page}`,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateOrderStatus = createAsyncThunk(
  "orderChecker/updateOrderStatus",
  async ({ orderId, remark, isReady }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ORDER_URL}/${orderId}`, {
        remark,
        isReady,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const searchOrders = createAsyncThunk(
  "orderChecker/searchOrders",
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ORDER_URL}/search`, searchParams);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchUsers = createAsyncThunk(
  "orderChecker/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(END_POINT_USERS_URL);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addOrder = createAsyncThunk(
  "orderChecker/addOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post(ORDER_URL, orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addQuickOrder = createAsyncThunk(
  "orderChecker/addQuickOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post(`${ORDER_URL}/quick/baking`, orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchProducts = createAsyncThunk(
  "orderChecker/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(END_POINT_PRODUCT + "/all");
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  users: [],
  orders: [],
  recentOrders: [],
  currentOrder: null,
  selectedOrderDetails: null,
  orderResponse: null,
  quickOrder: null,
  products: [],
  pagination: {
    page: 1,
    total: 0,
    totalPages: 0,
    limit: 12,
    hasNextPage: false,
    hasPrevPage: false,
  },
  status: {
    loading: false,
    error: null,
    loadingOrderDetails: false,
  },
};

const orderCheckerSlice = createSlice({
  name: "orderChecker",
  initialState,
  reducers: {
    resetError: (state) => {
      state.status.error = null;
      state.status.loading = false;
      state.currentOrder = null;
      state.orderResponse = null;
    },
    clearSelectedOrder: (state) => {
      state.selectedOrderDetails = null;
      state.status.loadingOrderDetails = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Order By ID
      .addCase(fetchOrderById.pending, (state) => {
        state.status.loadingOrderDetails = true;
        state.status.error = null;
        state.selectedOrderDetails = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.status.loadingOrderDetails = false;
        state.selectedOrderDetails = action.payload.result;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.status.loadingOrderDetails = false;
        state.status.error = action.payload.message;
        state.selectedOrderDetails = null;
      })
      // Search Orders
      .addCase(searchOrders.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(searchOrders.fulfilled, (state, action) => {
        state.status.loading = false;
        state.orders = action.payload.result;
      })
      .addCase(searchOrders.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      })
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status.loading = false;
        state.users = action.payload.result;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      })
      // Add Order
      .addCase(addOrder.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
        state.orderResponse = null;
      })
      .addCase(addOrder.fulfilled, (state, action) => {
        state.status.loading = false;
        state.orderResponse = action.payload.result;
      })
      .addCase(addOrder.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
        state.orderResponse = null;
      })
      // Add Quick Order
      .addCase(addQuickOrder.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
        state.quickOrder = null;
      })
      .addCase(addQuickOrder.fulfilled, (state, action) => {
        state.status.loading = false;
        state.quickOrder = action.payload.result;
      })
      .addCase(addQuickOrder.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
        state.quickOrder = null;
      })
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status.loading = false;
        state.products = action.payload.result;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      })
      // Fetch Recent Orders
      .addCase(fetchRecentOrders.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchRecentOrders.fulfilled, (state, action) => {
        state.status.loading = false;
        state.recentOrders = action.payload.result?.orders || [];

        // Extract pagination from the nested pagination object
        const paginationData = action.payload.result?.pagination || {};
        state.pagination = {
          page: paginationData.page || 1,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 0,
          limit: paginationData.limit || 12,
          hasNextPage: paginationData.hasNextPage || false,
          hasPrevPage: paginationData.hasPrevPage || false,
        };
      })
      .addCase(fetchRecentOrders.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      })
      // Update Order Status
      .addCase(updateOrderStatus.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.status.loading = false;
        // Remove the updated order from recentOrders since it's now ready
        state.recentOrders = state.recentOrders.filter(
          (order) => order.orderId !== action.payload.result.orderId,
        );
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.status.loading = false;
        state.status.error = action.payload.message;
      });
  },
});

export const { resetError, clearSelectedOrder } = orderCheckerSlice.actions;
export default orderCheckerSlice.reducer;
